import { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  type FreshStrawTransferImportResponse,
  useImportFreshStrawTransfersMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FreshStrawTransferImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional defaults pulled from the current session so the dialog only
   *  needs the file input in the common case. */
  defaultSiteId?: string;
  defaultFarmLocationId?: string;
  defaultTransferDate?: string;
  /** Fired after a successful import so the parent can refetch or surface
   *  a secondary confirmation. */
  onImported?: (result: FreshStrawTransferImportResponse) => void;
}

export function FreshStrawTransferImportDialog({
  open,
  onOpenChange,
  defaultSiteId,
  defaultFarmLocationId,
  defaultTransferDate,
  onImported,
}: FreshStrawTransferImportDialogProps) {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [siteId, setSiteId] = useState('');
  const [farmLocationId, setFarmLocationId] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [dairyComp, setDairyComp] = useState(false);
  const [result, setResult] = useState<FreshStrawTransferImportResponse | null>(
    null,
  );

  const [importTransfers, { isLoading }] =
    useImportFreshStrawTransfersMutation();

  useEffect(() => {
    if (open) {
      setFile(null);
      setSiteId(defaultSiteId ?? '');
      setFarmLocationId(defaultFarmLocationId ?? '');
      setTransferDate(defaultTransferDate ?? '');
      setDairyComp(false);
      setResult(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open, defaultSiteId, defaultFarmLocationId, defaultTransferDate]);

  const handleSubmit = async () => {
    if (!file) return;
    try {
      const response = await importTransfers({
        file,
        siteId: siteId || undefined,
        farmLocationId: farmLocationId || undefined,
        transferDate: transferDate || undefined,
        dairyComp,
      }).unwrap();

      setResult(response);
      onImported?.(response);

      const errorCount = response.errors?.length ?? 0;
      dispatch(
        addToast({
          severity: errorCount > 0 ? 'warn' : 'success',
          summary: 'Transfer import complete',
          detail: `Parsed ${response.parsed}, saved ${response.saved}${
            errorCount > 0
              ? `, ${errorCount} error${errorCount === 1 ? '' : 's'}`
              : ''
          }.`,
          life: ToastLife.LONG,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Import failed',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import transfer spreadsheet</DialogTitle>
          <DialogDescription>
            Upload an Excel workbook of transfers. Each row must have a Recip
            and either an EMBID or a Straw number. Missing site or transfer date
            on a row will fall back to the defaults below.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fresh-straw-import-file">Workbook (.xlsx)</Label>
            <Input
              ref={fileInputRef}
              id="fresh-straw-import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fresh-straw-import-site">
                Default site ID{' '}
                <span className="text-muted-foreground text-xs">
                  (optional)
                </span>
              </Label>
              <Input
                id="fresh-straw-import-site"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                placeholder="GUID"
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fresh-straw-import-farm">
                Default farm location{' '}
                <span className="text-muted-foreground text-xs">
                  (optional)
                </span>
              </Label>
              <Input
                id="fresh-straw-import-farm"
                value={farmLocationId}
                onChange={(e) => setFarmLocationId(e.target.value)}
                placeholder="GUID"
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="fresh-straw-import-date">
                Default transfer date{' '}
                <span className="text-muted-foreground text-xs">
                  (optional)
                </span>
              </Label>
              <Input
                id="fresh-straw-import-date"
                type="date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={dairyComp}
              onCheckedChange={(checked) => setDairyComp(checked === true)}
              disabled={isLoading}
              aria-label="Dairy Comp format"
            />
            <span>Dairy Comp export format</span>
          </label>

          {result && (
            <div
              className="border-border bg-muted/30 rounded-md border p-3 text-sm"
              data-testid="fresh-straw-import-result"
            >
              <div className="font-semibold">
                Parsed {result.parsed} · Saved {result.saved}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-destructive mt-2 list-disc space-y-0.5 pl-5 text-xs">
                  {result.errors.slice(0, 10).map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li>…and {result.errors.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!file || isLoading}
          >
            {isLoading ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
